import { Heading, MediumText, Text, tealDark600 } from "@RooUI";
import { zodResolver } from "@hookform/resolvers/zod";
import React, { useEffect, useState } from "react";
import { FormProvider, useForm, useFormContext, useFormState } from "react-hook-form";
import useFormPersist from "react-hook-form-persist";
import { useHistory } from "react-router-dom";

import { ControlledUserTypeRadioGroup } from "../../Common/Controllers/ControlledUserTypeGroups";
import { getInputErrorMessage } from "../../Common/Form/service";
import * as UserType from "../../constants/UserTypeConstants";
import veterinaryMedicineImg from "../../static/images/Veterinary Medicine.jpg";
import {
  Event,
  EventTracker,
  getEventEntityFromUserType,
} from "../../tracking/service/EventTracker/EventTrackerService";
import InformationalValueProps from "../InformationalValueProps";
import RegistrationPage from "../RegistrationPage";
import { HOSPITAL_REGISTRATION_LOCALSTORAGE_KEY } from "../constants";
import { useRegistrationContext } from "../registrationContext";
import { getWelcomeToRooRegistrationFormSchema } from "../registrationForm";
import {
  ButtonContainer,
  ContainerTopMargin,
  Image,
  RadioGroupContainer,
  RegistrationButton,
} from "../styles";
import { RegistrationForm } from "../types";

import HospitalInputs from "./HospitalInputs";
import ProviderInputs from "./ProviderInputs";

function WelcomeToRoo() {
  const history = useHistory();
  const { setRegistrationContext, registrationContext } = useRegistrationContext();
  const { handleSubmit, control, watch } = useFormContext<RegistrationForm>();
  const { errors } = useFormState<RegistrationForm>({ control });

  const selectedUserType = watch("selectedUserType");
  const [stepsCompleted, setStepsCompleted] = useState<number>(selectedUserType ? 1 : 0);

  const isHospitalUser = selectedUserType === UserType.HOSPITAL_USER_TYPE;

  const onContinue = (data: RegistrationForm) => {
    switch (selectedUserType) {
      case UserType.HOSPITAL_USER_TYPE:
        setRegistrationContext({ ...registrationContext, ...data });
        history.push({
          pathname: "/register/hospitalProfile",
        });
        break;
      case UserType.VET_USER_TYPE:
      case UserType.TECH_USER_TYPE:
        history.push({
          pathname: "/register/sign-up",
          state: {
            firstName: watch("firstName"),
            lastName: watch("lastName"),
            userType: watch("selectedUserType"),
          },
        });
        break;
      case UserType.STUDENT_USER_TYPE:
        history.push({
          pathname: "/registerStudent",
          state: {
            firstName: watch("firstName"),
            lastName: watch("lastName"),
          },
        });
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    EventTracker.send({
      eventName: Event.Name.VIEW_REGISTRATION_START,
      eventType: Event.Type.PAGE_VIEW,
      entityType: undefined,
      entityId: undefined,
    });
  }, []);

  return (
    <RegistrationPage
      progressBarPercent={(stepsCompleted / 4) * 100}
      title="Welcome to Roo"
      subtitle="Join the leading veterinary relief platform"
      rightContainerContent={
        <>
          {selectedUserType ? (
            <InformationalValueProps userType={selectedUserType} />
          ) : (
            <>
              <Image src={veterinaryMedicineImg} />
              <div>
                <Heading level={3} color={tealDark600}>
                  Experience the Joy of Relief
                </Heading>
                <MediumText>Connecting Vets & Techs with Hospitals for Relief Work</MediumText>
              </div>
            </>
          )}
        </>
      }
      leftContainerContent={
        <>
          <RadioGroupContainer>
            <ControlledUserTypeRadioGroup
              control={control}
              name="selectedUserType"
              label="Sign up as"
              // @ts-expect-error TS2322
              error={getInputErrorMessage(errors?.selectedUserType?.message)}
              onChange={(e) => {
                EventTracker.send({
                  eventName: Event.Name.SIGN_UP_AS,
                  eventType: Event.Type.CLICK,
                  // @ts-expect-error TS2322
                  entityType: getEventEntityFromUserType(e),
                  // @ts-expect-error TS2322
                  entityId: null,
                  context: {
                    userTypeId: e,
                  },
                });
                setTimeout(() => {
                  if (stepsCompleted === 0) {
                    setStepsCompleted(1);
                  }
                }, 300);
              }}
            />
          </RadioGroupContainer>
          {selectedUserType && (
            <>
              {/* @ts-expect-error TS2322 */}
              <HospitalInputs animate={!isHospitalUser} control={control} />
              <ProviderInputs animate={isHospitalUser} control={control} />
            </>
          )}
          <ButtonContainer>
            <RegistrationButton
              $buttonType="primary"
              form="registration-form"
              onClick={handleSubmit(onContinue)}
            >
              Continue
            </RegistrationButton>
          </ButtonContainer>
          <ContainerTopMargin>
            <Text>
              Already have an account? <a onClick={() => history.push("/login")}>Log in</a>
            </Text>
          </ContainerTopMargin>
        </>
      }
    />
  );
}

const WelcomeToRooContainer = () => {
  const methods = useForm<RegistrationForm>({
    resolver: zodResolver(getWelcomeToRooRegistrationFormSchema()),
  });

  useFormPersist(HOSPITAL_REGISTRATION_LOCALSTORAGE_KEY, {
    watch: methods.watch,
    setValue: methods.setValue,
    storage: window.localStorage,
  });

  return (
    <FormProvider {...methods}>
      <WelcomeToRoo />
    </FormProvider>
  );
};
// eslint-disable-next-line no-restricted-exports
export default WelcomeToRooContainer;
